import React from "react";
import {Link, useStaticQuery, graphql} from "gatsby";
import { getImage } from 'gatsby-plugin-image';

import { ReferenciaEvento } from "../../../interfaces/ReferenciaEvento";

// Utils //
import { splitButtonText } from "../../../utils/helpers";
import rawToReact from "../../../utils/rawToReact";

// Styles //
import "./eventCard.scss";
import contentfulRenderOptions from "../../../utils/contentfulRenderOption";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

const EventCard = (props: ReferenciaEvento) => {
    const {
        tituloEvento,
        descripcion,
        imagenPrincipal,
        tipoEvento,
        modoEvento,
        fechaEvento,
        horaEvento,
        lugarEvento,
        compartirLink,
        slug,
        onClickShare,
        setLinkUrl,
    } = props;

    const icons = useStaticQuery(graphql`
        query EVENTICONS {
            iconConversatorio: allContentfulAsset(
                filter: { title: { eq: "icon-conversatorio" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
            iconWebinar: allContentfulAsset(
                filter: { title: { eq: "icon-webinar" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
            iconMarterclass: allContentfulAsset(
                filter: { title: { eq: "icon-masterclass" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
            iconFeria: allContentfulAsset(
                filter: { title: { eq: "icon-feria" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
            iconMovil: allContentfulAsset(
                filter: { title: { eq: "icon-movil-empleo" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
            iconContratacion: allContentfulAsset(
                filter: { title: { eq: "icon-contratacion-masiva" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
            iconFeriasInstitucionales: allContentfulAsset(
                filter: { title: { eq: "icon-ferias-institucionales" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
            iconUsuario: allContentfulAsset(
                filter: { title: { eq: "icon-usuario" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
        }
    `);
    
    const iconConversatorio: string = icons.iconConversatorio.nodes[0].file.url;
    const iconWebinar: string = icons.iconWebinar.nodes[0].file.url;
    const iconMarterclass: string = icons.iconMarterclass.nodes[0].file.url;
    const iconFeria: string = icons.iconFeria.nodes[0].file.url;
    const iconMovil: string = icons.iconMovil.nodes[0].file.url;
    const iconContratacion: string = icons.iconContratacion.nodes[0].file.url;
    const iconFeriasInstitucionales: string = icons.iconFeriasInstitucionales.nodes[0].file.url;
    const iconUsuario: string = icons.iconUsuario.nodes[0].file.url;

    const switchIcons = (icon) => {
        switch (icon) {
            case "Conversatorio":
                return iconConversatorio;
            case "Feria de empleo":
                return iconFeria;
            case "Webinar":
                return iconWebinar;
            case "Mentoría":
                return iconConversatorio;
            case "Masterclass":
                return iconMarterclass;
            case "Contratación masiva":
                return iconContratacion;
            case "Móvil de empleo":
                return iconMovil;
            case "Ferias institucionales":
                return iconFeriasInstitucionales;
            default:
                return "";
        }
    }

    return (
        <div className="m-event-card">
            <div className="m-event-card__detail">
                <div className="m-event-card__detail__image">
                {imagenPrincipal && (
                    <img src={imagenPrincipal.file.url} alt={imagenPrincipal?.description || 'imagen Evento'} aria-hidden="true" />
                )}
                {tipoEvento && 
                    <div className="m-event-card__detail__image__tag">
                        <img src={switchIcons(tipoEvento)} alt={tipoEvento} />
                        <span>{tipoEvento}</span>
                    </div>
                }
                </div>
                <div className="m-event-card__detail__description">
                    {modoEvento && 
                        <span className="m-event-card__detail__description__tag">
                            <img src={iconUsuario} alt={modoEvento} />
                            {modoEvento}
                        </span>
                    }
                    <h2 className="m-event-card__detail__description__title">
                        {tituloEvento}
                    </h2>
                    <p className="m-event-card__detail__description__paragraph">{descripcion}</p>
                    {compartirLink && 
                        <div className="m-event-card__detail__description__shared">
                            <span onClick={() => {
                                onClickShare(true);
                                setLinkUrl(compartirLink);
                            }}>
                                <i className="icon-share-alt"></i> Compartir
                            </span>
                        </div>}
                </div>
            </div>
            <div className="m-event-card__schedule">
                <div className="m-event-card__schedule__container">
                    <div className="m-event-card__schedule__container__date">
                        <span>{splitButtonText(fechaEvento,0,",")}</span>
                        <p>
                            <strong>{splitButtonText(fechaEvento,1,",")}</strong>
                            <span>{splitButtonText(fechaEvento,2,",")}</span>
                        </p>
                    </div>
                    <div className="m-event-card__schedule__container__groupHourPlace">
                        <div className="m-event-card__schedule__container__groupHourPlace__hour">
                            <span>
                                <svg
                                    id="reloj"
                                    aria-label="Horario"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="21"
                                    height="21"
                                    viewBox="0 0 21 21"
                                >
                                    <path
                                        id="Trazado_76052"
                                        data-name="Trazado 76052"
                                        d="M10.5,0A10.5,10.5,0,1,0,21,10.5,10.5,10.5,0,0,0,10.5,0Zm0,19.25a8.75,8.75,0,1,1,8.75-8.75A8.75,8.75,0,0,1,10.5,19.25Z"
                                        transform="translate(0 0)"
                                        fill="#303030"
                                    />
                                    <path
                                        id="Trazado_76053"
                                        data-name="Trazado 76053"
                                        d="M12,6a1,1,0,0,0-1,1v4.325L7.629,13.437a1,1,0,0,0,1.062,1.7l3.84-2.4A1,1,0,0,0,13,11.879V7A1,1,0,0,0,12,6Z"
                                        transform="translate(-1.183 -1.224)"
                                        fill="#303030"
                                    />
                                </svg>
                            </span>
                            <p>{horaEvento}</p>
                        </div>
                        <div className="m-event-card__schedule__container__groupHourPlace__place">
                            <span>
                                <svg
                                    id="sitio"
                                    aria-label="Ubicación"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18.025"
                                    height="22.28"
                                    viewBox="0 0 18.025 22.28"
                                >
                                    <g id="Grupo_15515" data-name="Grupo 15515">
                                        <g id="Grupo_15514" data-name="Grupo 15514">
                                            <path
                                                id="Trazado_76055"
                                                data-name="Trazado 76055"
                                                d="M65.294,3.874a9,9,0,0,0-14.79,0,9,9,0,0,0-1.047,8.289,7.085,7.085,0,0,0,1.306,2.156l6.544,7.686a.778.778,0,0,0,1.186,0l6.542-7.684a7.093,7.093,0,0,0,1.306-2.153A9,9,0,0,0,65.294,3.874Zm-.413,7.749a5.557,5.557,0,0,1-1.028,1.683l0,0L57.9,20.3l-5.954-6.994a5.56,5.56,0,0,1-1.029-1.688,7.442,7.442,0,0,1,.87-6.858,7.438,7.438,0,0,1,12.227,0A7.444,7.444,0,0,1,64.881,11.623Z"
                                                transform="translate(-48.886)"
                                                fill="#303030"
                                            />
                                        </g>
                                    </g>
                                    <g
                                        id="Grupo_15517"
                                        data-name="Grupo 15517"
                                        transform="translate(4.28 4.37)"
                                    >
                                        <g id="Grupo_15516" data-name="Grupo 15516">
                                            <path
                                                id="Trazado_76056"
                                                data-name="Trazado 76056"
                                                d="M160.486,106.219a4.732,4.732,0,1,0,4.732,4.732A4.738,4.738,0,0,0,160.486,106.219Zm0,7.775a3.042,3.042,0,1,1,3.042-3.042A3.046,3.046,0,0,1,160.486,113.994Z"
                                                transform="translate(-155.754 -106.219)"
                                                fill="#303030"
                                            />
                                        </g>
                                    </g>
                                </svg>
                            </span>
                            <p>{lugarEvento}</p>
                        </div>
                    </div>
                </div>
                {slug && 
                    <div className="m-event-card__schedule__button">
                        <Link to={`./eventos/${slug}`}>Ver más</Link>
                    </div>
                }
            </div>
        </div>
    );
};

export default EventCard;
